/**
 * PaperTable — 시험지(/exam-papers) 목록의 표 모양(계약 §5-1 · authoring §6).
 *
 * 순수 프레젠테이션이다 — fetch·context 를 갖지 않는다. 좁은 폭에서는 8컬럼이 물리적으로
 * 못 들어가서, 담는 쪽(PaperListPanel)이 폭에 따라 이 표와 카드 목록을 갈라 쓴다.
 *
 * 정렬은 이제 서버가 한다(계약 §1-4 — GET /papers 에 sort·order 가 생겼다). 그런데 그 조작은
 * **여기가 아니라 담는 쪽(PaperListPanel)의 셀렉트**에 있다. 좁은 폭에서는 이 표 자체가
 * 카드 목록으로 갈리는데, 정렬을 표 머리에 붙이면 카드 모양에서는 정렬할 길이 사라진다 —
 * 두 모양이 같은 조작을 가지려면 조작이 표 밖에 있어야 한다.
 * (그 전에는 파라미터가 page/per_page/mine 뿐이라 머리를 눌러도 '현재 페이지 20건 안에서만'
 *  도는 정렬밖에 못 만들었고, 표는 그것을 전체 순서인 양 보여 줬다. 그래서 아예 안 넣었다.)
 *
 * 머리 정렬은 **담는 쪽이 `sort`·`onSort` 를 줄 때만** 선다(내림차순 → 오름차순 → 기본, common/tableSort).
 * 줄을 세우는 일은 담는 쪽이 한다 — 이 표는 받은 차례대로 그린다. 자리마다 세우는 곳이 달라서다:
 *   · 문제 상세 [시험지] 탭 — 목록을 통째로 받으므로 브라우저 안에서 세운다(「이 문제」·「배포」 값도 그쪽에만 있다).
 *   · 시험지 관리 목록 — 쪽 단위라 **서버가** 세운다. 서버 정렬 값이 있는 칸만 켜고(`sortableKeys`),
 *     머리는 위 셀렉트와 같은 값을 읽고 쓴다. 카드 모양에서도 셀렉트가 남아 있어 정렬할 길이 사라지지 않는다.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Copy, Trash2, Sparkles, FolderInput, Files, Folder, Inbox, Check, Users, Eye, Loader2,
} from 'lucide-react';
import SortableTh from '../common/SortableTh';
import { numSortValue, textSortValue, timeSortValue } from '../common/tableSort';
import { buildFolderTree, fmtServerDateTime, buildFolderPathIndex } from './examPaperApi';
/* 판정과 문구는 folderOwnership 한 곳이 쥔다 — 여기서 `permission !== 'view'` 를 다시 쓰거나
   말을 새로 지으면 같은 사실이 두 벌이 되고, 한쪽만 고쳐지는 날이 온다. */
import { canPutPapersNode, PAPER_LOCK } from './folderOwnership';

export const paperIdOf = (p) => p?.paper_id ?? p?.id;

/**
 * 드래그 페이로드 MIME — 폴더 쪽이 **dragover 중에** "이게 시험지 드래그인가"를 알아야 한다.
 * 그때는 보안상 getData 가 막히고 types 만 읽을 수 있어서 전용 이름이 필요하다
 * (text/plain 만 쓰면 브라우저 밖에서 끌어온 아무 글자에도 폴더가 반응한다).
 */
export const PAPER_DND_MIME = 'application/x-ky-paper';

/** 문항 수 = item_count (계약 §2-7 확정). 뒤 폴백은 요약이 덜 채워져 오는 응답 대비 */
export const paperItemCount = (p) => p?.item_count ?? p?.items_count
  ?? (Array.isArray(p?.items) ? p.items.length : null);

/**
 * 학생에게 출제한 횟수 — **살아 있는 배정 행 수**(soft 삭제 제외, 마감분 포함).
 * 같은 학생에게 두 번 줬으면 2다: 회차가 아니라 행을 센다(BE 회신 2026-09-01 §1 2-3 · 오너 확정 2026-09-05).
 *
 * 목록 응답(`GET /papers`)에 **항상 실린다**(BE-20260905-reply §1 2-1, dev `85e67f7`) — 배정이
 * 없으면 `0` 이다. **예약(아직 오픈 전)도 이 수에 든다**(같은 회신 §2 말미): 기준은
 * `is_deleted=false` 하나뿐이라 예약·진행·마감이 모두 한 행으로 셈해진다.
 *
 * 그런데도 **키가 없으면 null 이다 — 0 으로 떨어뜨리지 않는다.** 아직 안 받은 서버는 이 키를
 * 통째로 안 싣고(회신 §1 2-3), 그때 0 으로 뭉개면 배지가 사라져 화면이 「한 번도 안 나갔다」고
 * 거짓말한다. 「안 나갔다」와 「모른다」는 다른 말이다.
 */
export const paperAssignCount = (p) => {
  if (p == null || p.assign_count == null) return null;
  const n = Number(p.assign_count);
  return Number.isInteger(n) ? n : null;
};

/** 서버 시각 표기 — 문자열을 자르면 오프셋·'Z' 가 통째로 버려져 UTC 가 지역시간인 척한다 */
export const fmtPaperDate = (s) => fmtServerDateTime(s);

/**
 * 작성자 — 서버가 싣는 키는 **`author_name`** 이다(BE-20260829-reply-fe-0828.md §3 2-1).
 * 값은 사람 이름(`user_name`)이고, 그것이 비어 있으면 서버가 로그인 id 를 대신 넣어 준다 —
 * 즉 「이름이냐 아이디냐」는 서버가 이미 접어 준다. 화면이 갈라야 할 것은 아래 셋뿐이다.
 *   ① 문자열이 왔다 → 그대로 그린다
 *   ② `null` 이 왔다 → 만든 사람이 **지워졌거나 확인되지 않는 계정**이다(계약이 못박은 갈래)
 *   ③ 키 자체가 없다 → 이 응답에 작성자가 **아직 안 실린 것**이다(필드 배포 전 옛 응답)
 * (2026-08-29 이전에는 이 자리가 `p.author` 를 읽어, 값이 와도 열이 영영 빈칸이었다.)
 */
export const paperAuthorName = (p) => (typeof p?.author_name === 'string' && p.author_name.trim() !== ''
  ? p.author_name
  : null);
/** 작성자 칸이 빈 이유 — ②와 ③은 그릴 이름이 없다는 점만 같고 뜻이 다르다. 글자는 같아도 사유는 가른다 */
export const paperAuthorEmptyReason = (p) => (p != null && 'author_name' in p
  ? '만든 사람이 지워졌거나 확인되지 않습니다'
  : '작성자가 아직 실려 오지 않았습니다');

/**
 * 폴더 칸의 글자 — 「부모 › 자식」 경로. 계약 §2 말미가 실어 주는 것은 folder_name **하나**다. 폴더가 계층이 된
 * 뒤로는 이름만으로 소속을 말할 수 없어(「2학기」가 두 줄 설 수 있다) 트리가 올려 준 평면 목록으로 경로를 만든다.
 * 경로를 못 만들면(목록이 아직 안 왔거나 그 폴더가 목록에 없으면) 이름으로 받친다. 둘 다 없으면 null(칸에는 「없음」).
 * 칸과 정렬이 같은 함수를 읽는다 — 갈리면 보이는 글자와 다른 차례로 줄이 선다.
 * `folderPaths` 는 buildFolderPathIndex(folders) 의 결과다.
 */
export function paperFolderLabel(p, folderPaths) {
  const path = folderPaths?.get(String(p?.folder_id ?? '')) || null;
  const names = path && path.length > 0 ? path : [p?.folder_name].filter(Boolean);
  return names.length === 0 ? null : names.join(' › ');
}

/**
 * 머리 정렬의 값 — 이 표가 아는 칸만 답한다. 「이 문제」·「배포」는 담는 쪽의 값이라 null 이다
 * (담는 쪽이 그 두 칸을 먼저 가로채 답한다, ProblemHubV2/PaperUsageTab).
 */
export function paperSortValue(p, key, folderPaths) {
  switch (key) {
    case 'id': return numSortValue(paperIdOf(p));
    case 'title': return textSortValue(p?.title);
    case 'items': return numSortValue(paperItemCount(p));
    case 'folder': return paperFolderLabel(p, folderPaths);
    case 'author_name': return paperAuthorName(p);
    case 'updated': return timeSortValue(p?.updated_at || p?.created_at);
    default: return null;
  }
}

/**
 * 제목 뒤에 붙는 한 줄 요약 — 목록 머리줄과 접힌 상세 손잡이가 **같은 함수**를 쓴다.
 * 두 곳이 각자 만들면 같은 시험지가 자리에 따라 다른 문장으로 보인다.
 * 없는 값은 '—' 로 채우지 않고 통째로 뺀다(한 줄에 늘어서는 자리라 빈 조각이 곧 소음이다).
 * 그래서 작성자도 여기서는 ②·③을 가르지 않는다 — 사유를 말할 자리(title)가 없는 줄이다.
 */
export function paperHeadMeta(p) {
  if (p == null) return '';
  const n = paperItemCount(p);
  const when = fmtPaperDate(p.updated_at || p.created_at);
  return [
    n != null ? `문항 ${n}개` : null,
    p.revision != null ? `rev ${p.revision}` : null,
    p.folder_name || '폴더 없음',
    paperAuthorName(p),
    when || null,
  ].filter(Boolean).join(' · ');
}

/** 행 액션은 이름이 같은 버튼이 세로로 스무 개 늘어선다 — 읽어 주는 이름에 시험지를 넣는다 */
const ACTION_TONE = {
  neutral: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
  violet: 'text-violet-700 hover:bg-violet-100',
  danger: 'text-danger hover:bg-red-50',
};

/**
 * `disabled`·`title` 을 받는다 — 잠기는 자리가 생겼기 때문이다(아래 [이동]).
 * **감추지 않고 잠근다**: 버튼이 사라지면 「왜 없지」로 끝나고, 잠기면 title 이 이유를 말한다.
 * 잠긴 얼굴(`disabled:opacity-40`)은 이 화면의 다른 잠긴 손잡이와 같은 값이다
 * (FolderTree 의 [⋯] 버튼 · 「옮길 곳 고르기」 후보) — 두 화면이 같은 뜻을 다른 얼굴로 말하면 안 된다.
 * title 기본값이 label 인 것은 종전 그대로다: 이유가 없을 때 이름을 지우면 손잡이가 말을 잃는다.
 */
function RowAction({
  icon: Icon, label, subject, onClick, tone = 'neutral', disabled = false, title, busy = false,
}) {
  return (
    <button
      type="button"
      onClick={(e) => onClick(e)}
      disabled={disabled}
      aria-busy={busy || undefined}
      aria-label={`${subject} ${label}`}
      title={title || label}
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 text-[11px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${ACTION_TONE[tone]}`}
    >
      <Icon aria-hidden="true" className={`size-3 ${busy ? 'animate-spin' : ''}`} />
      {label}
    </button>
  );
}


/**
 * 깊이 들여쓰기 — **아이콘째로 민다.** 이름 앞에 공백(nbsp)만 넣던 때는 아이콘이 한 열에 서 있어
 * 깊이가 눈에 잡히지 않았다(오너 지적 2026-08-28). 파일 트리가 다 그렇게 하는 데는 이유가 있다.
 * 한 단 12px — 폴더 트리의 자식 들여쓰기(pl-3)와 같은 값이라 두 화면의 계단이 어긋나지 않는다.
 * 클래스를 문자열로 늘어놓는 이유: Tailwind 는 소스를 글자로 훑어 쓸 클래스를 정한다(계산식은 못 읽는다).
 */
const INDENT_BY_LEVEL = ['pl-2', 'pl-5', 'pl-8', 'pl-11'];
const indentOf = (level) => INDENT_BY_LEVEL[Math.min(Math.max(level, 0), INDENT_BY_LEVEL.length - 1)];

/**
 * MovePopover — [이동] 옆에 붙는 미니 모달. 폴더를 **고르는 순간 옮긴다**(확인 단계 없음).
 *
 * body 로 내보내(portal) 화면에 고정한다. 표를 감싼 칸이 overflow-x-auto 라 그 안에 두면
 * 팝오버가 칸 경계에서 잘리고, 가로 스크롤바가 생겨 목록이 통째로 밀린다.
 *
 * 스크롤·크기변경에는 **따라다니지 않고 닫는다.** 버튼 위치를 열 때 한 번 재서 쓰는데,
 * 표가 스크롤되면 그 값이 거짓이 된다 — 엉뚱한 자리에 떠 있는 것보다 사라지는 편이 낫다.
 *
 * 폴더가 계층이 된 뒤(계약 §2, 깊이 최대 3)로는 목록도 계층 순서로 늘어놓고 깊이만큼 들여쓴다.
 * **고른 값의 뜻은 그대로다**: 정수(그 폴더) 아니면 null(무소속).
 *
 * 볼 수만 있는 폴더는 **잠근 채로 남긴다**(빼지 않는다) — 목록에서 지우면 「내가 아는 그 폴더가
 * 왜 없지」가 되고, 잠그면 왜 못 고르는지를 화면이 말한다. FolderTree 의 「옮길 곳 고르기」 겹이
 * 이미 그렇게 한다 — 같은 화면의 두 「옮길 곳」이 다른 얼굴을 하면 안 되므로 그 얼굴을 그대로 쓴다.
 */
function MovePopover({ paper, rect, folders, onPick, onClose }) {
  const boxRef = useRef(null);
  // 재기 전에는 화면 밖에 둔다 — 왼쪽 위에 한 번 그렸다가 제자리로 뛰는 깜빡임을 없앤다
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (el == null) return;
    const { offsetWidth: w, offsetHeight: h } = el;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - w - 8));
    const below = rect.bottom + 6;
    // 아래가 모자라면 버튼 위로 뒤집는다 — 마지막 행에서 목록이 화면 밖으로 나가지 않게.
    // 뒤집은 뒤에도 위아래를 한 번 더 조인다. 창이 아주 낮으면 뒤집은 자리마저 화면을 벗어난다.
    const want = below + h <= window.innerHeight - 8 ? below : rect.top - h - 6;
    const top = Math.max(8, Math.min(want, window.innerHeight - h - 8));
    setPos({ left, top });
  }, [rect]);

  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onClose);
    window.addEventListener('scroll', onClose, true); // true — 표 안쪽 스크롤도 잡는다
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [onClose]);

  const cur = paper.folder_id ?? null;
  const rowCls = 'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors';

  /**
   * 서버가 준 **평면** 목록을 계층 순서로 접어 위에서부터 훑어 늘어놓는다(계약 §2).
   * 평면 순서 그대로 그리면 **부모가 다른 동명 폴더가 구별되지 않는다** — 「2학기」가 두 줄 서 있는데
   * 어느 것이 어느 부모 밑인지 화면에 남는 단서가 없다.
   * 접는 규칙은 examPaperApi 한 곳(buildFolderTree)이 쥔다 — 여기서 다시 만들면 트리 화면과
   * 순서가 어긋난다. 순수 함수라 이 컴포넌트가 fetch·context 를 갖지 않는다는 성격은 그대로다.
   * 맨 앞 '없음'은 폴더가 아니라 「폴더에서 빼기」라 트리 밖에 따로 세운다(깊이 0).
   */
  const options = useMemo(() => {
    const flat = [{ node: { name: '없음' }, level: 0, unfiled: true }];
    const walk = (nodes, level) => {
      (Array.isArray(nodes) ? nodes : []).forEach((n) => {
        flat.push({ node: n, level });
        walk(n.children, level + 1);
      });
    };
    walk(buildFolderTree(Array.isArray(folders) ? folders : []), 0);
    return flat;
  }, [folders]);

  return createPortal(
    <div
      ref={boxRef}
      role="dialog"
      aria-modal="false"
      aria-label={`${paper.title || `#${paperIdOf(paper)}`} 폴더로 이동`}
      className="fixed w-56 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-2xl"
      style={{
        zIndex: 'var(--z-dropdown)',
        left: pos ? pos.left : -9999,
        top: pos ? pos.top : -9999,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      <p className="px-2 pb-1 pt-0.5 text-[10px] font-medium text-gray-400">폴더로 이동</p>
      <div className="max-h-64 overflow-y-auto">
        {options.map(({ node: f, level, unfiled }) => {
          const fid = unfiled ? null : (f.folder_id ?? f.id ?? null);
          const here = String(fid) === String(cur);
          const Icon = unfiled ? Inbox : Folder;
          /* 넣을 수 있는 자리인가 — 여기의 축은 **도착지**다(행을 뺄 수 있는지는 아래 canTakeOut 이
             따로 본다). 계약이 닫는 것은 등급 view 하나뿐이다(§2-3 — move 는 owner·edit 만).
             「없음(폴더에서 빼기)」은 폴더가 아니라 빼는 자리라 잠그지 않는다: 빼기를 막는 일은
             행의 드래그 손잡이와 [이동] 버튼이 맡는다. */
          const blocked = !unfiled && !canPutPapersNode(f);
          return (
            <button
              key={unfiled ? 'none' : fid}
              type="button"
              // 지금 있는 폴더를 다시 고르면 아무 일도 안 일어나야 한다 — 빈 요청을 보내면
              // 서버에 판(revision)만 쌓이고 화면은 그대로다
              onClick={() => (here ? onClose() : onPick(fid))}
              disabled={blocked}
              title={blocked ? PAPER_LOCK.title : undefined}
              className={`${rowCls} ${indentOf(level)} disabled:cursor-not-allowed disabled:opacity-40 ${here ? 'bg-primary/10 text-gray-900' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <Icon aria-hidden="true" className="size-3.5 shrink-0 text-gray-400" />
              <span className="min-w-0 flex-1 truncate">{f.name || '(이름 없음)'}</span>
              {here && <Check aria-hidden="true" className="size-3.5 shrink-0 text-primary" />}
              {/* 짧은 사유는 오른쪽 끝에 붙인다 — 자리도 글자 크기도 FolderTree 의 후보와 같다 */}
              {!here && blocked && <span className="shrink-0 text-[9px] text-gray-400">{PAPER_LOCK.reason}</span>}
              {!here && !blocked && <span className="sr-only">로 옮기기</span>}
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}

const COL = {
  id: { key: 'id', label: '#ID', className: 'min-w-12 w-[5%]' },
  title: { key: 'title', label: '시험지', className: 'min-w-48 w-auto' },
  // 이 표가 **문제 쪽에서** 불릴 때만 서는 칸 — 「그 문제가 이 시험지의 몇 번인가」.
  // 문항수와 같은 폭·같은 정렬을 쓴다(둘 다 한 자리 숫자에 '개'/'번'이 붙는 값이라 갈릴 이유가 없다).
  matched: { key: 'matched', label: '이 문제', className: 'min-w-16 w-[8%]' },
  items: { key: 'items', label: '문항수', className: 'min-w-16 w-[8%]' },
  // 역시 문제 쪽에서만 서는 칸 — 「이 시험지가 학생에게 몇 번 나갔나」. 목록 요약에는 이 값이 없고
  // `?problem_id=` 역조회 응답에만 assign_count 로 실린다(problem-hub-v2/BE-20260901-reply §1 2-3).
  assign: { key: 'assign', label: '배포', className: 'min-w-16 w-[8%]' },
  folder: { key: 'folder', label: '폴더', className: 'min-w-[4.5rem] w-[8%] max-w-32' },
  // key 는 서버 필드 이름과 맞춰 둔다 — 그리는 자리(아래 칸)와 같은 이름이라야 다음 사람이 되짚는다
  author_name: { key: 'author_name', label: '작성자', className: 'min-w-16 w-[10%]' },
  updated: { key: 'updated', label: '수정일', className: 'min-w-[7rem] w-[12%]' },
  // 버튼만 든 칸이라 줄 세울 값이 없다
  actions: { key: 'actions', label: '작업', className: 'min-w-[19rem] w-[24%]', sortable: false },
};

/**
 * 선택 전용 좁은 자리의 열 폭. 시험지 V2의 정보 구조와 행 모양은 유지하되, 관리 작업이 없는
 * 6열 표가 제목·폴더 같은 긴 문자열 때문에 불필요하게 넓어지지 않도록 고정 레이아웃에서 쓴다.
 * 합계 456px — 이보다 좁은 모바일에서만 부모가 가로 스크롤하고, 데스크톱의 반쪽 패널에서는
 * 남는 폭을 제목 열에 나눠 쓴다. 원본 COL은 그대로 두어 V2 목록의 폭 계약에는 영향이 없다.
 */
const COMPACT_COL_CLASS = {
  id: 'w-10',
  title: 'w-36',
  matched: 'w-12',
  items: 'w-[3.25rem]',
  assign: 'w-12',
  folder: 'w-14',
  author_name: 'w-[3.75rem]',
  updated: 'w-[6.5rem]',
  actions: COL.actions.className,
};

/**
 * 이 자리에서 설 칸을 고른다 — **모양이 아니라 칸의 유무만** 자리마다 다르다.
 *
 * 「작업」은 넘겨받은 액션이 하나도 없으면 세우지 않는다. 빈 칸이 `min-w-[19rem]`(304px)을
 * 그대로 먹어서, 좁은 자리(문제 상세 탭)에서는 **아무것도 없는 칸 때문에 표가 가로로 넘친다.**
 * 「이 문제」는 반대로 `matchedLabelOf` 를 받았을 때만, 「배포」는 `assignLabelOf` 를 받았을 때만 선다.
 * 머리와 몸이 같은 판단을 쓰도록 한 곳에서 만든다 — 갈리면 표가 통째로 어긋난다.
 */
function columnsFor({ withMatched, withAssign, withActions }) {
  const cols = [COL.id, COL.title];
  if (withMatched) cols.push(COL.matched);
  cols.push(COL.items);
  if (withAssign) cols.push(COL.assign);
  cols.push(COL.folder, COL.author_name, COL.updated);
  if (withActions) cols.push(COL.actions);
  return cols;
}

function PaperTable({
  items = [],
  empty = null,              // 0건일 때 표 안에 세울 안내(머리는 남긴다)
  selectedId = null,
  onSelect,
  onPreview,
  /* 목록 행에서 시험지 상세를 읽어 인쇄 데이터를 만드는 중인 id. 값이 있는 동안 다른 행도
     잠가 두 번의 상세 응답이 서로의 미리보기를 덮지 않게 한다. */
  previewBusyId = null,
  onAiCopy,
  /* 삭제. **누르기 전에 막지 않는다** — 계약 §10 은 "배정 수>0 이면 버튼 비활성"을 권한다.
     못 하는 게 아니라 **아직 안 한 것**이다: 목록에 배정 수가 안 와서 못 막던 사정은
     2026-09-05 회신(§1 2-1)으로 사라졌고, 지금은 같은 응답의 `assign_count` 로 가릴 수 있다.
     버튼을 잠그는 일은 별건으로 남겼다(요청서 §1-3 — 숫자를 세우는 일과 조작을 막는 일은
     다른 판단이라 오너에게 따로 여쭙기로 했다). 그때까지는 확인 대화상자가 미리 말하고,
     실제 차단은 409 has_assignments 로 돌아온다(담는 쪽이 describeApiError 로 문구를 만든다). */
  onDelete,
  onMoveTo,                  // (paper, folderId|null) — 미니 모달에서 고른 즉시 부른다
  folders = [],
  onCopy,
  /* (paper) => '3번' | null — 주면 「이 문제」 칸이 선다. 문제 상세 [시험지] 탭이 쓴다.
     표가 **시험지 목록이자 「이 문제가 든 시험지」 목록**으로 두 자리에서 쓰이는데,
     행의 생김새는 한 벌이어야 해서 사본을 뜨지 않고 칸 하나를 옵션으로 뒀다. */
  matchedLabelOf = null,
  /* (paper) => '2회' | <미배포> | null — 주면 「배포」 칸이 선다. 같은 탭이 쓴다.
     null 은 「값이 안 왔다」('—')이고 0회는 0회다 — 두 뜻을 한 글자로 뭉개지 않는 것은 부르는 쪽 몫이다. */
  assignLabelOf = null,
  /* (paper) => 정수 | null — 학생에게 **출제한 횟수**. 배정 행을 그대로 센다: 같은 학생에게
     두 번 줬으면 2다(오너 확정 2026-09-05). null 은 「값이 안 왔다」이고 0 은 0회다 —
     배지는 1 이상일 때만 뜨므로 두 경우의 **그림이 같다.** 사유는 아이콘 title 이 가른다
     (작성자 칸이 '—' 의 사유를 title 로 가르는 것과 같은 규칙). */
  assignCountOf = null,
  /* (paperId) => void — 주면 제목 앞에 「학생 출제」 아이콘이 선다. 누르면 그 시험지의
     [학생 출제] 탭으로 간다. 안 주면 아이콘 자체가 서지 않는다 — 문제 상세 [시험지] 탭은
     시험지 화면 밖이라 갈 곳이 없어 안 준다. */
  onOpenAssign = null,
  /* 관리 작업이 없는 선택창처럼 폭이 제한된 자리에서만 쓰는 밀도. 기본값은 V2 표 그대로다. */
  compact = false,
  /* parent면 이 컴포넌트 안에 가로 스크롤을 만들지 않고, 바깥 한 요소가 x/y를 함께 맡는다. */
  scrollOwner = 'self',
  /* 머리 정렬 — `{ key, order }` | null 과 (key) => void. 둘 다 주면 머리를 눌러 줄을 세운다(머리 주석).
     items 는 이미 그 차례로 세워서 넘긴다. 안 주면 머리는 종전 그대로 글자뿐이다. */
  sort = null,
  onSort = null,
  /* 머리를 켤 칸의 key 목록. 안 주면 「작업」 말고 전부다. 서버가 세우는 자리(시험지 관리 목록)는
     서버에 정렬 값이 있는 칸만 넘긴다 — 나머지 칸을 켜면 한 쪽 안에서만 도는 정렬이 된다(머리 주석). */
  sortableKeys = null,
}) {
  const withActions = !!(onPreview || onMoveTo || onCopy || onAiCopy || onDelete);
  const withMatched = !!matchedLabelOf;
  const withAssign = !!assignLabelOf;
  const columns = useMemo(
    () => columnsFor({ withMatched, withAssign, withActions }),
    [withMatched, withAssign, withActions],
  );
  const cellPadding = compact ? 'px-2 py-2' : 'px-3 py-2';
  // 폴더 경로 지도 — 행이 스무 줄이라 행마다 다시 만들지 않는다
  const folderPaths = useMemo(() => buildFolderPathIndex(folders), [folders]);
  /**
   * 폴더 id → 그 폴더 항목. 행이 **자기가 지금 든 폴더의 등급**을 봐야 하는데(아래 canTakeOut),
   * 시험지 요약에는 등급이 실려 오지 않는다 — `permission` 은 폴더 항목에만 있다(계약 §2-3).
   * 새로 조회하지 않는다: 이미 prop 으로 들어온 같은 `folders` 를 한 번 더 훑을 뿐이다.
   * 위 folderPaths 와 같은 결로 두는 이유도 같다 — 행마다 목록을 훑으면 행 수 × 폴더 수가 된다.
   */
  const folderById = useMemo(() => {
    const m = new Map();
    (Array.isArray(folders) ? folders : []).forEach((f) => {
      const id = f?.folder_id ?? f?.id;
      if (id != null) m.set(String(id), f);
    });
    return m;
  }, [folders]);
  // 지금 [이동] 미니 모달이 열려 있는 행 — {paper, rect}. rect 는 연 순간의 버튼 자리다
  const [moveFor, setMoveFor] = useState(null);
  /**
   * 방금 누른 자리가 액션 칸이었나 — 행 드래그가 버튼 클릭을 삼키는 것을 막는 유일한 단서다.
   *
   * 행 전체가 draggable 이라, 버튼 위에서 마우스가 **몇 px 만 흔들려도** 브라우저는 그것을
   * '드래그 시작'으로 읽고 **click 을 통째로 버린다**(실측: 제자리면 눌리고 6px 이면 안 눌린다).
   * 트랙패드에서는 이 흔들림이 예사라, 버튼이 "가끔 안 먹는" 게 아니라 대체로 안 먹는다.
   *
   * 액션 칸에 draggable={false} 를 주는 것으로는 못 막는다 — 드래그 원본을 찾는 눈은 위로
   * 올라가 <tr> 을 집고, dragstart 는 **그 <tr> 에서** 터진다. 칸에 건 preventDefault 는
   * 실행될 기회조차 없다(실측: 칸의 핸들러는 한 번도 안 불렸고 행의 것만 불렸다).
   * 그래서 누른 자리를 pointerdown 때 기억해 뒀다가 행의 dragstart 에서 취소한다.
   */
  const pressedInActionsRef = useRef(false);
  // 목록이 접히면 보이는 줄이 몇 개로 줄어든다 — 고른 행이 그 밖에 있으면 아래 상세가
  // 어디서 왔는지 화면에 남지 않는다. 접히는 동안(높이 320ms)에는 재도 소용없어 끝나고 잰다.
  const activeRowRef = useRef(null);
  useEffect(() => {
    if (selectedId == null) return undefined;
    const t = setTimeout(() => {
      // block:'nearest' — 이미 보이면 건드리지 않는다. 'start' 는 볼 때마다 목록을 튀게 한다
      activeRowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 340);
    return () => clearTimeout(t);
  }, [selectedId, items]);

  return (
    <div
      data-paper-table-frame=""
      className={`${scrollOwner === 'self' ? 'overflow-x-auto' : ''} ${compact ? 'min-w-[28.5rem]' : ''} rounded-xl border`}
    >
      <table className={`w-full text-sm ${compact ? 'table-fixed' : ''}`}>
        <thead className="bg-gray-50">
          <tr>
            {columns.map((c) => {
              const thClass = `whitespace-nowrap ${cellPadding} text-center ${compact ? 'text-[11px]' : 'text-xs'} font-medium tracking-wider text-gray-500 ${compact ? COMPACT_COL_CLASS[c.key] : c.className}`;
              if (onSort && c.sortable !== false && (sortableKeys == null || sortableKeys.includes(c.key))) {
                return (
                  <SortableTh key={c.key} sortKey={c.key} sort={sort} onSort={onSort} className={thClass}>
                    {c.label}
                  </SortableTh>
                );
              }
              return <th key={c.key} scope="col" className={thClass}>{c.label}</th>;
            })}
          </tr>
        </thead>
        <tbody className="bg-white">
          {/* 0건이어도 표를 걷지 않는다 — 머리가 사라지면 「목록이 아직 안 왔나」와
              「이 폴더가 비었나」가 같은 화면으로 보인다. 폴더를 눌러 다니는 동안
              표 자리가 그대로 있어야 어디를 보고 있는지 놓치지 않는다. */}
          {items.length === 0 && (
            <tr>
              <td colSpan={columns.length} className={`${compact ? 'px-2' : 'px-3'} py-10 text-center`}>
                {empty}
              </td>
            </tr>
          )}
          {items.map((p) => {
            const id = paperIdOf(p);
            const active = String(id) === String(selectedId);
            // 읽어 줄 이름 — 제목 없는 시험지도 행끼리 구분은 돼야 하므로 #ID 로 떨어진다
            const subject = p.title || `#${id}`;
            const count = paperItemCount(p);
            const previewPending = previewBusyId != null;
            const previewBusyHere = previewPending && String(previewBusyId) === String(id);
            /**
             * 이 행을 지금 폴더에서 **뺄 수 있는가** — 축은 등급(permission)이지 `is_mine` 이 아니다.
             * 매니저는 남의 폴더도 항상 edit 이고 서버가 허용한다(§2-1) — is_mine 으로 막으면
             * 되던 일이 안 되고, 왜 안 되는지가 화면 어디에도 없다.
             * 무소속 행(folder_id 없음)과 목록에 없는 폴더는 **참**이다 — folderOwnership 의
             * 「모르면 잠그지 않는다」와 같은 태도다. 운영 배포분 응답에는 아직 permission 키가
             * 없어 전부 참이고, 그래서 이 변경으로 **화면이 한 곳도 바뀌지 않는다.**
             */
            const canTakeOut = canPutPapersNode(
              p.folder_id == null ? null : folderById.get(String(p.folder_id)),
            );
            return (
              <tr
                key={id}
                ref={active ? activeRowRef : null}
                /* 드래그의 목적지는 폴더 트리 하나뿐이다 — 트리가 없는 자리(문제 상세 [시험지] 탭)
                   에서 켜 두면 끌리기는 하는데 놓을 데가 없는 죽은 손잡이가 된다.

                   그리고 여기가 **「빼기 금지」를 덮는 유일한 자리다.** dragover 중에는 보안상
                   getData 가 막혀 types 만 읽힌다(위 PAPER_DND_MIME 주석) — 놓는 쪽(폴더)은
                   「이 시험지가 어느 폴더에서 왔는지」를 알 수 없어 보기 전용 폴더에서 빼는 것을
                   막을 방법이 없다. 그래서 끌기 시작을 막는다. 폴더 쪽도 같은 짝이다
                   (FolderTree 의 폴더 이름 버튼 `draggable={… && mine}`).

                   끌 수 없게 된 행은 **왜 안 되는지 말해야 한다** — 끌리는 행과 겉이 같으면
                   사용자는 마우스가 고장난 줄 안다. 이 파일이 이미 쓰는 방법(title)으로 붙인다. */
                draggable={!!onMoveTo && canTakeOut}
                title={canTakeOut ? undefined : PAPER_LOCK.takeOutTitle}
                onPointerDown={(e) => {
                  pressedInActionsRef.current = !!e.target?.closest?.('[data-row-actions]');
                }}
                onDragStart={(e) => {
                  // 액션 버튼에서 시작된 몸짓은 드래그가 아니다 — 취소해야 click 이 살아난다
                  if (pressedInActionsRef.current) { e.preventDefault(); return; }
                  // 폴더가 "이미 그 폴더인가"를 drop 시점에 가릴 수 있게 소속도 함께 싣는다
                  const payload = JSON.stringify({ id, folderId: p.folder_id ?? null, title: subject });
                  e.dataTransfer.setData(PAPER_DND_MIME, payload);
                  e.dataTransfer.setData('text/plain', subject); // 폴백(브라우저 밖으로 끌 때)
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onClick={() => onSelect && onSelect(id)}
                className={`cursor-pointer border-b border-gray-100 transition-colors duration-150 last:border-b-0 ${
                  active ? 'bg-primary/15' : 'hover:bg-primary/10'
                }`}
              >
                {/* 왼쪽 초록 띠 — 아래 상세 카드가 「이 행에서 열린 것」임을 말하는 표시다.
                    비선택 행에도 같은 두께를 투명으로 둬야 고를 때 글자가 옆으로 밀리지 않는다 */}
                <td
                  className={`whitespace-nowrap border-l-[3px] ${cellPadding} font-mono text-xs text-gray-400 ${
                    active ? 'border-primary' : 'border-transparent'
                  }`}
                >
                  #{id}
                </td>
                <td className={`${cellPadding} ${compact ? 'overflow-hidden' : ''}`}>
                  {/* 출처·판 둘 다 제목에 붙는 꼬리표다 — 칸을 따로 주면 대부분 비어 있거나 한
                      자리 숫자인 값에 폭이 묶이고, 눈은 제목에서 표 끝까지 갔다 와야 그것을 안다.
                      순서는 출처 → 판 — 「무엇으로 만든 것인가」가 「몇 번째 판인가」보다 앞선다 */}
                  <span className={`flex min-w-0 items-center gap-1.5 ${compact ? 'overflow-hidden' : ''}`}>
                    {/* 학생 출제 — 얼굴은 프리셋 표의 「시험 정보」 버튼을 그대로 가져왔다
                        (PresetPage/PresetTable.js — 아이콘·배지 자리·99+ 까지). 같은 뜻의 자리를
                        두 화면이 다른 얼굴로 쓰면 배지가 무엇을 세는 숫자인지 매번 다시 배워야 한다.
                        `data-row-actions` — 행이 draggable 이라 이 표식이 없으면 마우스가 몇 px
                        흔들린 순간 click 이 통째로 버려진다(위 pressedInActionsRef 주석의 실측). */}
                    {onOpenAssign && (() => {
                      const raw = assignCountOf ? assignCountOf(p) : null;
                      const n = Number.isInteger(raw) ? raw : null;
                      const label = n == null
                        ? '학생 출제 — 횟수가 아직 실려 오지 않습니다'
                        : (n === 0 ? '학생 출제 — 아직 없음' : `학생 출제 ${n}회`);
                      return (
                        <button
                          type="button"
                          data-row-actions=""
                          onClick={(e) => { e.stopPropagation(); onOpenAssign(id); }}
                          title={label}
                          aria-label={`${subject} ${label}`}
                          /* mr-2 — 배지가 버튼 오른쪽으로 8px(-right-2) 튀어나온다. 줄의 기본 간격
                             (gap-1.5 = 6px)만으로는 모자라 **배지가 제목 첫 글자를 덮었다**
                             (실측 1440px: 「내신기출…」의 '내', 「아주 많이…」의 '아'). 6+8=14px 을
                             둬 배지 오른쪽 끝과 제목 사이에 6px 이 남는다. 배지가 넓어져도(99+)
                             오른쪽 정렬이라 돌출은 8px 그대로다. */
                          className="relative mr-2 shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        >
                          <Users aria-hidden="true" className="size-4" />
                          {n != null && n > 0 && (
                            <span className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-white">
                              {n > 99 ? '99+' : n}
                            </span>
                          )}
                        </button>
                      );
                    })()}
                    <span className="line-clamp-1 min-w-0 font-medium text-gray-900" title={p.title || ''}>
                      {p.title || <span className="font-normal text-gray-300">제목 없음</span>}
                    </span>
                    {/* 문항을 AI 가 쓴 시험지라는 표시. 출생 기록이라 뒤에 사람이 고쳐도 남는다.
                        계보(derived_from)는 자리가 없어 배지의 title 로 내렸다 — 카드 목록에는
                        「원본 #N」이 글자로 남아 있다(PaperListPanel) */}
                    {p.is_ai_copy && (
                      <span
                        title={p.derived_from != null ? `AI 변형 — 원본 #${p.derived_from}` : 'AI 변형'}
                        className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700"
                      >
                        <Sparkles aria-hidden="true" className="size-2.5" />
                        AI 변형
                      </span>
                    )}
                    {p.revision != null && (
                      <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">
                        rev {p.revision}
                      </span>
                    )}
                  </span>
                </td>
                {withMatched && (() => {
                  const label = matchedLabelOf(p);
                  return (
                    <td className={`whitespace-nowrap ${cellPadding} text-right text-gray-900`}>
                      {label == null ? <span className="text-gray-300">—</span> : label}
                    </td>
                  );
                })()}
                <td className={`whitespace-nowrap ${cellPadding} text-right text-gray-900`}>
                  {count == null ? <span className="text-gray-300">—</span> : `${count}개`}
                </td>
                {withAssign && (() => {
                  const label = assignLabelOf(p);
                  return (
                    <td className={`whitespace-nowrap ${cellPadding} text-right text-gray-900`}>
                      {label == null ? <span className="text-gray-300">—</span> : label}
                    </td>
                  );
                })()}
                <td className={`max-w-32 overflow-hidden whitespace-nowrap ${cellPadding} text-xs`}>
                  {/* 글자는 「부모 › 자식」 경로다(paperFolderLabel 주석). 모양은 종전 알약 그대로 — 글자만 경로다. */}
                  {(() => {
                    const label = paperFolderLabel(p, folderPaths);
                    if (label == null) {
                      return <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-500">없음</span>;
                    }
                    return (
                      <span
                        title={label}
                        className="inline-block max-w-full truncate rounded-full bg-amber-50 px-2 py-0.5 align-middle text-amber-700"
                      >
                        {label}
                      </span>
                    );
                  })()}
                </td>
                <td className={`whitespace-nowrap ${cellPadding} text-center text-gray-600 ${compact ? 'overflow-hidden' : ''}`}>
                  {/* 세 갈래를 뭉개지 않는다(paperAuthorName 주석) — 그릴 이름이 없을 때의 글자는
                      같은 '—' 지만, **왜 비었는지**를 title 로 갈라 말한다. 「사람이 지워졌다」와
                      「값이 아직 안 온다」가 같은 말이 되면 다음 사람이 배포 여부를 오독한다. */}
                  {(() => {
                    const name = paperAuthorName(p);
                    if (name != null) {
                      return (
                        <span title={name} className={compact ? 'block truncate' : ''}>{name}</span>
                      );
                    }
                    return <span className="text-gray-300" title={paperAuthorEmptyReason(p)}>—</span>;
                  })()}
                </td>
                <td className={`whitespace-nowrap ${cellPadding} ${compact ? 'text-[10px]' : 'text-xs'} text-gray-500`}>
                  {fmtPaperDate(p.updated_at || p.created_at) || <span className="text-gray-300">—</span>}
                </td>
                {/* 행 클릭이 곧 [열기]라, 버튼을 누른 것이 여는 것으로도 읽히면 안 된다 */}
                {/* data-row-actions — 위 pressedInActionsRef 가 이 표식으로 자리를 가린다.
                    여기 있던 draggable={false} 와 onDragStart 는 걷었다: dragstart 가 <tr> 에서
                    터지므로 이 칸의 핸들러는 한 번도 불리지 않았고, 막는 일을 하지 못했다 */}
                {withActions && (
                <td
                  data-row-actions=""
                  className={`${cellPadding} whitespace-nowrap`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex flex-nowrap items-center gap-0.5">
                    {onPreview && (
                      <RowAction
                        icon={previewBusyHere ? Loader2 : Eye}
                        label="미리보기"
                        subject={subject}
                        onClick={() => onPreview(p)}
                        busy={previewBusyHere}
                        disabled={previewPending || count === 0}
                        title={count === 0
                          ? '문항이 없어 미리볼 수 없습니다'
                          : previewBusyHere
                            ? '인쇄 미리보기를 준비하고 있습니다'
                            : previewPending
                              ? '다른 시험지의 인쇄 미리보기를 준비하고 있습니다'
                              : '미리보기'}
                      />
                    )}
                    {onMoveTo && (
                      <RowAction
                        icon={FolderInput}
                        label="이동"
                        subject={subject}
                        /* 보기 전용 폴더에 든 행은 뺄 수 없다 — **감추지 않고 잠근다**(RowAction 주석).
                           이게 잠기면 아래 미니 모달은 열리지도 않는다(여는 길이 이 버튼 하나다).
                           그래도 모달 안 후보를 함께 잠그는 것은 **축이 달라서다** — 여기는
                           「이 행을 뺄 수 있나」(출발지)이고, 저기는 「그 폴더에 넣을 수 있나」(도착지)다. */
                        disabled={!canTakeOut}
                        title={canTakeOut ? undefined : PAPER_LOCK.takeOutTitle}
                        // 버튼 자리는 여기서 재 둔다 — 팝오버가 body 에 붙어 조상 좌표를 못 쓴다
                        onClick={(e) => setMoveFor({ paper: p, rect: e.currentTarget.getBoundingClientRect() })}
                      />
                    )}
                    {onCopy && (
                      <RowAction icon={Files} label="복사" subject={subject} onClick={() => onCopy(p)} />
                    )}
                    {onAiCopy && (
                      <RowAction icon={Copy} label="AI변형" subject={subject} tone="violet" onClick={() => onAiCopy(p)} />
                    )}
                    {onDelete && (
                      <RowAction icon={Trash2} label="삭제" subject={subject} tone="danger" onClick={() => onDelete(p)} />
                    )}
                  </div>
                </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {moveFor && (
        <MovePopover
          paper={moveFor.paper}
          rect={moveFor.rect}
          folders={folders}
          onClose={() => setMoveFor(null)}
          onPick={(fid) => { setMoveFor(null); onMoveTo(moveFor.paper, fid); }}
        />
      )}
    </div>
  );
}

export default PaperTable;
